import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import "./styles/Checkout.css";
import "../styles/global.css";

const emptyAddressForm = {
  house: "", address: "", landmark: "", city: "",
  pincode: "", state: "", recipient: "", phone: "", orderFor: "self",
};

// ── Custom confirm/alert popup ─────────────────────────────
const CustomDialog = ({ open, title, message, type, onConfirm, onCancel }) => {
  if (!open) return null;
  const isConfirm = type === "confirm";
  return (
    <div className="cdialog-overlay">
      <div className="cdialog-box">
        <div className={`cdialog-icon-wrap cdialog-icon--${isConfirm ? "warn" : "success"}`}>
          <i className={`fa-solid ${isConfirm ? "fa-triangle-exclamation" : "fa-circle-check"}`}></i>
        </div>
        <h5 className="cdialog-title">{title}</h5>
        <p className="cdialog-msg">{message}</p>
        <div className="cdialog-actions">
          {isConfirm && (
            <button className="cdialog-btn cdialog-btn--cancel" onClick={onCancel}>Cancel</button>
          )}
          <button className={`cdialog-btn cdialog-btn--${isConfirm ? "danger" : "primary"}`} onClick={onConfirm}>
            {isConfirm ? "Yes, Delete" : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Prescription upload card ───────────────────────────────
const PrescriptionUploadBox = ({ prescriptionFiles, onFiles, onRemove }) => {
  const fileRef = useRef();
  return (
    <div className="rx-upload-section w-100">
      <div className="rx-dropzone" onClick={() => fileRef.current?.click()}>
        <div className="rx-dropzone-icon">
          <i className="fa-solid fa-cloud-arrow-up"></i>
        </div>
        <p className="rx-dropzone-title">Click to upload prescription</p>
        <p className="rx-dropzone-sub">JPG, PNG, PDF up to 10MB</p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          className="rx-file-input"
          onChange={onFiles}
        />
      </div>
      {prescriptionFiles.length > 0 && (
        <div className="rx-files-list">
          {prescriptionFiles.map((file, idx) => (
            <div key={idx} className="rx-file-chip">
              <i className={`fa-solid ${file.type.includes("pdf") ? "fa-file-pdf" : "fa-file-image"} rx-file-chip-icon`}></i>
              <span className="rx-file-chip-name">{file.name}</span>
              <button className="rx-file-chip-remove" onClick={() => onRemove(idx)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Coupon box ─────────────────────────────────────────────
const CouponSection = ({
  appliedCoupon, couponInput, setCouponInput,
  couponLoading, couponDiscount, applyCoupon, removeCoupon,
  availableCoupons = [],
}) => {
  const [showAllCoupons, setShowAllCoupons] = useState(false);

  // Coupons this user has already redeemed are excluded entirely — they
  // shouldn't clutter the list. If a customer manually types an already-used
  // code, the backend still rejects it and the existing toast shows the
  // real message ("You have already used this coupon code.").
  const visibleCoupons = availableCoupons.filter((c) => !c.already_used);

  const handleApplyFromModal = (code) => {
    applyCoupon(code);
    setShowAllCoupons(false);
  };

  return (
    <div className="coupon-wrap w-100">
      <div className="coupon-label-row">
        <i className="fa-solid fa-tag coupon-tag-icon"></i>
        <span className="coupon-label">Have a Coupon?</span>
      </div>
      {!appliedCoupon ? (
        <div className="coupon-field-wrap">
          <input
            type="text"
            className="coupon-input"
            placeholder="Enter coupon code"
            value={couponInput}
            onChange={e => setCouponInput(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === "Enter") applyCoupon(); }}
          />
          <button className="coupon-apply-btn" onClick={() => applyCoupon()} disabled={couponLoading}>
            {couponLoading ? <i className="fa-solid fa-spinner fa-spin"></i> : "Apply"}
          </button>
        </div>
      ) : (
        <div className="coupon-applied-wrap">
          <div className="coupon-applied-info">
            <i className="fa-solid fa-circle-check coupon-check-icon"></i>
            <div>
              <span className="coupon-code-badge">{appliedCoupon}</span>
              <p className="coupon-saved-text">You saved ₹{couponDiscount.toFixed(2)}!</p>
            </div>
          </div>
          <button className="coupon-remove-btn" onClick={removeCoupon}>
            <i className="fa-solid fa-xmark"></i> Remove
          </button>
        </div>
      )}

      {/* Everything shown here (code, value, min order) comes straight
          from the backend; nothing is hardcoded on the frontend. Button is
          always visible so it's obvious it's there — the modal itself
          explains if there's nothing to show yet. */}
      {!appliedCoupon && (
        <button
          type="button"
          className="coupon-view-all-btn"
          onClick={() => setShowAllCoupons(true)}
        >
          <i className="fa-solid fa-tags"></i> View All Coupons
          {visibleCoupons.length > 0 ? ` (${visibleCoupons.length})` : ""}
        </button>
      )}

      {showAllCoupons && (
        <div className="coupon-modal-overlay" onClick={() => setShowAllCoupons(false)}>
          <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coupon-modal-header">
              <p className="coupon-available-title">Available Coupons</p>
              <button
                type="button"
                className="coupon-modal-close"
                onClick={() => setShowAllCoupons(false)}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            {visibleCoupons.length === 0 ? (
              <p className="coupon-empty-state">
                No coupons available right now. Check back later!
              </p>
            ) : (
            <div className="coupon-available-list">
              {visibleCoupons.map((c) => (
                <div className="coupon-available-item" key={c.code}>
                  <div>
                    <span className="coupon-available-code">{c.code}</span>
                    <span className="coupon-available-desc">
                      {c.type === "percent" ? `Get ${c.discount}% off` : `Get ₹${c.discount} off`}
                      {" · "}Min booking amount: ₹{c.min_order}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="coupon-apply-btn"
                    disabled={couponLoading}
                    onClick={() => handleApplyFromModal(c.code)}
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Bill summary ───────────────────────────────────────────
const BillRows = ({ cartAmount, couponDiscount, appliedCoupon, finalAmount, deliveryCharges }) => (
  <ul className="price-list">
    <li>Total MRP</li>
    <li>₹{cartAmount.toFixed(2)}</li>
    <li>Delivery charges</li>
    <li>{deliveryCharges === 0 ? "FREE" : `₹${deliveryCharges}`}</li>
    <li>Discount on MRP</li>
    <li>₹0.00</li>
    {couponDiscount > 0 && <>
      <li className="bill-coupon-label">Coupon ({appliedCoupon})</li>
      <li className="bill-coupon-value">- ₹{couponDiscount.toFixed(2)}</li>
    </>}
    <li className="bill-final-label">Amount to be paid</li>
    <li className="bill-final-value">₹{finalAmount.toFixed(2)}</li>
  </ul>
);

const Checkout = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [cartAmount, setCartAmount] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newPrescription, setNewPrescription] = useState(null);
  const [pastPrescription, setPastPrescription] = useState(null);
  const [prescriptionFiles, setPrescriptionFiles] = useState([]);

  // ── Address modals ─────────────────────────────────────────
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editForm, setEditForm] = useState(emptyAddressForm);

  // ── Coupon ─────────────────────────────────────────────────
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);

  // ── Delivery charges ───────────────────────────────────────
  const [deliveryCharges, setDeliveryCharges] = useState(0);

  // ── Validation errors ──────────────────────────────────────
  const [errors, setErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  // ── Custom dialog ──────────────────────────────────────────
  const [dialog, setDialog] = useState({ open: false, title: "", message: "", type: "confirm", onConfirm: null });

  const showConfirm = (title, message, onConfirm) =>
    setDialog({ open: true, title, message, type: "confirm", onConfirm });
  const showAlert = (title, message) =>
    setDialog({ open: true, title, message, type: "alert", onConfirm: () => setDialog(d => ({ ...d, open: false })) });
  const closeDialog = () => setDialog(d => ({ ...d, open: false }));

  const finalAmount = Math.max(0, cartAmount + deliveryCharges - couponDiscount);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) { deviceId = uuidv4(); localStorage.setItem("device_id", deviceId); }

  // ── Settings ───────────────────────────────────────────────
  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
      const data = await res.json();
      if (data.status) setDeliveryCharges(Number(data.data.delivery_charges) || 0);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  // ── Address form handlers ──────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    if (editErrors[name]) {
      setEditErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const openAddModal = () => {
    setAddressForm(emptyAddressForm);
    setErrors({});
    setShowAddressModal(true);
  };

  const openEditModal = (address) => {
    setEditingAddress(address);
    setEditForm({
      house: address.house_flat || "",
      address: address.addressinfo || "",
      landmark: address.landmark || "",
      city: address.city || "",
      pincode: address.pincode || "",
      state: address.state || "",
      recipient: address.recipient || "",
      phone: address.mobile_number || "",
      orderFor: address.order_for || "self",
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  // ── Prescription ───────────────────────────────────────────
  const handlePrescriptionFiles = (e) => {
    const files = Array.from(e.target.files);
    setPrescriptionFiles(prev => [...prev, ...files]);
  };

  const removePrescriptionFile = (idx) =>
    setPrescriptionFiles(prev => prev.filter((_, i) => i !== idx));

  // ── API ────────────────────────────────────────────────────
  const getCart = () => {
    fetch(`${API_BASE_URL}api/getCart`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: localStorage.getItem("device_id") }),
    })
      .then(r => r.json())
      .then(json => {
        setCartCount(json.count);
        const items = Object.values(json.cart);
        const total = items.reduce((sum, item) => {
          const price = item.dprice ? item.price - (item.price * item.dprice / 100) : item.price;
          return sum + price * item.qty;
        }, 0);
        setCartAmount(total);
      })
      .catch(console.error);
  };

  const getAddress = () => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/getAddress?userid=${userid}`)
      .then(r => r.json())
      .then(json => { if (json?.data) setAddresses(Object.values(json.data)); })
      .catch(console.error);
  };

  const validateAddressData = (data) => {
    const newErrors = {};
    if (!data.house?.trim()) newErrors.house = "House / Flat number is required";
    if (!data.address?.trim()) newErrors.address = "Address is required";
    else if (data.address.trim().length < 5) newErrors.address = "Address must be at least 5 characters";
    if (!data.city?.trim()) newErrors.city = "City is required";
    if (!data.state?.trim()) newErrors.state = "State is required";
    if (!data.pincode?.trim()) newErrors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(data.pincode.trim())) newErrors.pincode = "Pincode must be 6 digits";
    if (!data.orderFor) newErrors.orderFor = "Please select who this order is for";
    if (!data.recipient?.trim()) newErrors.recipient = "Recipient name is required";
    else if (data.recipient.trim().length < 2) newErrors.recipient = "Recipient name is too short";
    else if (!/^[A-Za-z\s.'-]+$/.test(data.recipient.trim())) newErrors.recipient = "Recipient name can only contain letters";
    if (!data.phone?.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[6-9]\d{9}$/.test(data.phone.trim())) newErrors.phone = "Enter a valid 10-digit mobile number";
    return newErrors;
  };

  const saveAddress = (e) => {
    e.preventDefault();
    const newErrors = validateAddressData(addressForm);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/saveAddress`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userid, ...addressForm }),
    })
      .then(r => r.json())
      .then(json => {
        if (json.status) {
          if (json?.data) {
            const list = Object.values(json.data);
            setAddresses(list);
            const last = list[list.length - 1];
            if (last) setSelectedAddressId(last.id);
          }
          setAddressForm(emptyAddressForm);
          setErrors({});
          setShowAddressModal(false);
          showAlert("Address Saved", "Your new address has been saved and selected.");
        } else {
          showAlert("Save Failed", json.message || "Could not save address.");
        }
      })
      .catch(console.error);
  };

  const handleUpdateAddress = (e) => {
    e.preventDefault();
    const newErrors = validateAddressData(editForm);
    setEditErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/updateAddress`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userid, address_id: editingAddress.id, ...editForm }),
    })
      .then(r => r.json())
      .then(json => {
        if (json.status) {
          if (json?.data) setAddresses(Object.values(json.data)); else getAddress();
          setShowEditModal(false);
          setEditingAddress(null);
          setEditErrors({});
          showAlert("Address Updated", "Your address has been updated successfully.");
        } else {
          showAlert("Update Failed", json.message || "Something went wrong.");
        }
      })
      .catch(console.error);
  };

  const handleDeleteAddress = (id) => {
    showConfirm(
      "Delete Address",
      "Are you sure you want to delete this address? This cannot be undone.",
      () => {
        closeDialog();
        const userid = localStorage.getItem("user_id");
        fetch(`${API_BASE_URL}api/deleteAddress`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userid, address_id: id }),
        })
          .then(r => r.json())
          .then(json => {
            if (json.status) {
              if (json?.data) setAddresses(Object.values(json.data));
              else setAddresses(prev => prev.filter(a => a.id !== id));
              if (selectedAddressId === id) setSelectedAddressId(null);
              showAlert("Deleted", "Address has been removed.");
            }
          })
          .catch(console.error);
      }
    );
  };

  // ── Coupon ─────────────────────────────────────────────────
  const authToken = localStorage.getItem("authToken");
  const [availableCoupons, setAvailableCoupons] = useState([]);

  const fetchAvailableCoupons = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/coupons_front`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      const data = await res.json();
      if (data.status) setAvailableCoupons(data.data || []);
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
  };

  const applyCoupon = async (codeOverride) => {
    const code = (codeOverride || couponInput).trim().toUpperCase();
    if (!code) { toast.error("Enter a coupon code"); return; }
    if (!authToken) { toast.error("Please login to apply a coupon"); return; }
    setCouponLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/applyCoupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ coupon: code, amount: cartAmount, for: "order" }),
      });
      const data = await res.json();
      if (data.status) {
        setAppliedCoupon(code);
        setCouponDiscount(Number(data.discount));
        toast.success("Coupon applied!");
      } else {
        toast.error(data.message || "Invalid coupon code");
      }
    } catch { toast.error("Failed to apply coupon"); }
    finally { setCouponLoading(false); }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponInput("");
    toast.success("Coupon removed");
  };

  // ── Place order ────────────────────────────────────────────
  const placeOrder = async () => {
    const userid = localStorage.getItem("user_id");
    if (!selectedAddressId) { toast.error("Please select an address"); return; }
    const formData = new FormData();
    formData.append("userid", userid);
    formData.append("deviceId", deviceId);
    formData.append("address_id", selectedAddressId);
    if (appliedCoupon) {
      formData.append("coupon", appliedCoupon);
    }
    formData.append("delivery_charges", deliveryCharges);
    if (newPrescription) formData.append("new_prescription", newPrescription);
    if (pastPrescription) formData.append("past_prescription", pastPrescription);
    prescriptionFiles.forEach((f, i) => formData.append(`prescription_${i}`, f));
    try {
      const response = await fetch(`${API_BASE_URL}api/placeOrder`, {
        method: "POST",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status === false) {
        // Surfaces backend validation messages too — e.g. a coupon that
        // became invalid (expired / already used) between "Apply" and
        // "Place Order" is caught here as well, since the backend
        // re-validates the coupon from scratch at order time.
        toast.error(data.message || "Order failed");
        if (data.message && /coupon/i.test(data.message)) removeCoupon();
        return;
      }

      // A coupon that fully covers the order total means there's nothing
      // to charge — the backend already marked it Paid and skipped
      // Cashfree entirely, so there's no payment session to open here.
      if (data.free) {
        toast.success("Order placed successfully");
        navigate(`/order-success?order_id=${data.order_id}&free=1`);
        return;
      }

      if (!data.payment_session_id) {
        toast.error("Could not start payment. Please try again.");
        return;
      }

      const cashfree = window.Cashfree({ mode: "production" });
      cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (error) { console.error(error); toast.error("Server error"); }
  };

  // ── Single useEffect — no duplicate calls ──────────────────
  useEffect(() => {
    getCart();
    getAddress();
    fetchSettings();
    fetchAvailableCoupons();
  }, []);

  const couponProps = {
    appliedCoupon, couponInput, setCouponInput,
    couponLoading, couponDiscount, applyCoupon, removeCoupon,
    availableCoupons,
  };

  return (
    <>
      <Header />
      <section className="py-xl-5 py-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Checkout</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          PC VIEW
      ═══════════════════════════════ */}
      <section className="cart-item-block cart-item-block-1 cart-product-wiith-item d-none d-lg-block">
        <div className="container">
          <div className="row">

            {/* Left */}
            <div className="col-12 col-xl-8 mb-xl-0 mb-3">
              <div className="card-border mb-3">
                <div className="cart-title mb-0">{cartCount} Item in your Cart</div>
              </div>

              {/* Prescription */}
              <div className="card-border w-100 mb-0 card-header_1 mb-4">
                <div className="cart-title dark-text">Upload Prescription</div>
                <span className="small-text">How would you like to proceed?</span>

                <div className="card-border card-border-1 w-100">
                  <div className="d-flex align-items-start justify-content-between w-100">
                    <div>
                      <span className="small-text mb-0">I don't have a prescription</span>
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="form-check check-box-1">
                          <input className="form-check-input" type="checkbox" id="prescriptionCheck" defaultChecked />
                        </div>
                        <p className="text-dark mb-0">Get a Free consultation by a licensed Doctor</p>
                      </div>
                    </div>
                    <div className="form-check radio-btn p-0">
                      <input className="form-check-input rounded-circle radio" type="radio" name="prescription" id="rxNo" />
                    </div>
                  </div>
                </div>

                <div className="card-border card-border-1 w-100 flex-column align-items-start">
                  <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                    <div>
                      <span className="small-text mb-0 d-block">I have a prescription</span>
                      <span className="small-text mb-0 d-block mt-1">What is a valid prescription?</span>
                    </div>
                    <div className="form-check radio-btn p-0">
                      <input className="form-check-input rounded-circle radio" type="radio" name="prescription" id="rxYes" />
                    </div>
                  </div>
                  <PrescriptionUploadBox
                    prescriptionFiles={prescriptionFiles}
                    onFiles={handlePrescriptionFiles}
                    onRemove={removePrescriptionFile}
                  />
                </div>
              </div>

              {/* Order summary */}
              <div className="card-border card-border-1 w-100">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-4 w-100">
                  <div className="cart-title text-grey mb-0">Cart total : <b>₹{finalAmount.toFixed(2)}</b></div>
                  <span>Payment Pending</span>
                </div>
                <div className="card-border card-border-1 w-100 flex-column align-items-start justify-content-start gap-3">
                  <p>Shipment Item : {cartCount}</p>
                </div>
                <button className="dc-book-btn py-3 w-100 d-flex align-items-center justify-content-center" onClick={placeOrder}>
                  <span className="text-white">Place Order To Pay ₹{finalAmount.toFixed(2)}</span>
                </button>
              </div>
            </div>

            {/* Right */}
            <div className="col-12 col-xl-4 mb-4">

              {/* Addresses */}
              <div className="card-border px-0">
                <div className="d-flex align-items-center justify-content-between px-3">
                  <div className="cart-title mb-0">Address Details</div>
                </div>
                <hr className="w-100" />
                <div className="px-3 w-100">
                  <div className="card-border border-0 p-0">
                    {addresses?.map(address => (
                      <div
                        key={address.id}
                        className={`chk-addr-card ${selectedAddressId === address.id ? "chk-addr-card--selected" : ""}`}
                        onClick={() => setSelectedAddressId(address.id)}
                      >
                        <div className="chk-addr-card-top">
                          <div className="chk-addr-card-left">
                            <input
                              className="form-check-input rounded-circle radio"
                              type="radio" name="deliveryAddress" value={address.id}
                              checked={selectedAddressId === address.id}
                              onChange={() => setSelectedAddressId(address.id)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className="chk-addr-name">{address.recipient}</span>
                          </div>
                          <div className="chk-addr-actions" onClick={e => e.stopPropagation()}>
                            <button className="chk-addr-edit-btn" onClick={() => openEditModal(address)}>
                              <i className="fa-solid fa-pen"></i>
                            </button>
                            <button className="chk-addr-delete-btn" onClick={() => handleDeleteAddress(address.id)}>
                              <i className="far fa-trash-can"></i>
                            </button>
                          </div>
                        </div>
                        <p className="chk-addr-text">{address.house_flat}, {address.addressinfo}, {address.city}, {address.state} - {address.pincode}</p>
                      </div>
                    ))}
                  </div>
                  <button className="chk-add-addr-btn" onClick={openAddModal}>
                    <i className="fa-solid fa-plus"></i> Add New Address
                  </button>
                </div>
              </div>

              {/* Coupon */}
              <div className="card-border px-3 py-3">
                <CouponSection {...couponProps} />
              </div>

              {/* Bill */}
              <div className="card-border px-0">
                <div className="d-flex align-items-center justify-content-between px-3">
                  <div className="cart-title mb-0">Bill Summary</div>
                </div>
                <hr className="w-100" />
                <BillRows
                  cartAmount={cartAmount}
                  couponDiscount={couponDiscount}
                  appliedCoupon={appliedCoupon}
                  finalAmount={finalAmount}
                  deliveryCharges={deliveryCharges}
                />
              </div>
              <div className="w-100 border-1 px-0">
                <button className="dc-book-btn w-100 py-3 d-flex align-items-center justify-content-center" onClick={placeOrder}>
                  <span>Proceed to Pay <b>₹{finalAmount.toFixed(2)}</b></span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          MOBILE VIEW
      ═══════════════════════════════ */}
      <section className="d-block d-lg-none py-3">
        <div className="container checkout-mobile">

          <div className="cmo-card">
            <div className="cmo-card-header">
              <p className="cmo-card-title"><i className="fa-solid fa-cart-shopping me-2"></i>Your Cart</p>
              <span className="cmo-cart-pill">{cartCount} item{cartCount !== 1 ? "s" : ""}</span>
            </div>
            <div className="cmo-card-body">
              <div className="cmo-cart-total-row">
                <span className="cmo-cart-total-label">Total</span>
                <span className="cmo-cart-total-amount">₹{finalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="cmo-card">
            <div className="cmo-card-header">
              <p className="cmo-card-title"><i className="fa-solid fa-file-medical me-2"></i>Prescription</p>
            </div>
            <div className="cmo-card-body">
              <div className="cmo-rx-option">
                <div>
                  <p className="cmo-rx-title">I don't have a prescription</p>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <input className="form-check-input" type="checkbox" defaultChecked />
                    <span className="cmo-rx-sub">Get a Free Doctor consultation</span>
                  </div>
                </div>
                <input className="form-check-input rounded-circle radio flex-shrink-0" type="radio" name="rxMobile" />
              </div>
              <div className="cmo-rx-option flex-column align-items-start">
                <div className="d-flex w-100 justify-content-between align-items-start mb-2">
                  <p className="cmo-rx-title mb-0">I have a prescription</p>
                  <input className="form-check-input rounded-circle radio flex-shrink-0" type="radio" name="rxMobile" />
                </div>
                <PrescriptionUploadBox
                  prescriptionFiles={prescriptionFiles}
                  onFiles={handlePrescriptionFiles}
                  onRemove={removePrescriptionFile}
                />
              </div>
            </div>
          </div>

          <div className="cmo-card">
            <div className="cmo-card-header">
              <p className="cmo-card-title"><i className="fa-solid fa-location-dot me-2"></i>Delivery Address</p>
            </div>
            <div className="cmo-card-body">
              {addresses?.map(address => (
                <div
                  key={address.id}
                  className={`chk-addr-card ${selectedAddressId === address.id ? "chk-addr-card--selected" : ""}`}
                  onClick={() => setSelectedAddressId(address.id)}
                >
                  <div className="chk-addr-card-top">
                    <div className="chk-addr-card-left">
                      <input
                        className="form-check-input rounded-circle radio"
                        type="radio" name="deliveryAddressMobile" value={address.id}
                        checked={selectedAddressId === address.id}
                        onChange={() => setSelectedAddressId(address.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="chk-addr-name">{address.recipient}</span>
                    </div>
                    <div className="chk-addr-actions" onClick={e => e.stopPropagation()}>
                      <button className="chk-addr-edit-btn" onClick={() => openEditModal(address)}><i className="fa-solid fa-pen"></i></button>
                      <button className="chk-addr-delete-btn" onClick={() => handleDeleteAddress(address.id)}><i className="far fa-trash-can"></i></button>
                    </div>
                  </div>
                  <p className="chk-addr-text">{address.addressinfo}, {address.city}, {address.state} - {address.pincode}</p>
                </div>
              ))}
              <button className="chk-add-addr-btn" onClick={openAddModal}>
                <i className="fa-solid fa-plus"></i> Add New Address
              </button>
            </div>
          </div>

          <div className="cmo-card">
            <div className="cmo-card-body">
              <CouponSection {...couponProps} />
            </div>
          </div>

          <div className="cmo-card">
            <div className="cmo-card-header">
              <p className="cmo-card-title"><i className="fa-solid fa-receipt me-2"></i>Bill Summary</p>
            </div>
            <div className="cmo-card-body">
              <div className="cmo-bill-row"><span>Total MRP</span><span>₹{cartAmount.toFixed(2)}</span></div>
              <div className="cmo-bill-row"><span>Delivery</span><span>{deliveryCharges === 0 ? "FREE" : `₹${deliveryCharges}`}</span></div>
              <div className="cmo-bill-row"><span>Discount on MRP</span><span>₹0.00</span></div>
              {couponDiscount > 0 && (
                <div className="cmo-bill-row cmo-bill-coupon">
                  <span>Coupon ({appliedCoupon})</span>
                  <span>- ₹{couponDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="cmo-bill-row"><span>Amount to be Paid</span><span>₹{finalAmount.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="cmo-sticky-bar">
            <button className="dc-book-btn w-100 justify-content-center d-flex align-items-center" onClick={placeOrder}>
              <i className="fa-solid fa-lock"></i>
              <span>Place Order &nbsp;·&nbsp; ₹{finalAmount.toFixed(2)}</span>
            </button>
          </div>

        </div>
      </section>

      {/* ADD ADDRESS MODAL */}
      {showAddressModal && (
        <div className="addr-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="addr-modal" onClick={e => e.stopPropagation()}>
            <div className="addr-modal-header">
              <div className="addr-modal-header-left">
                <div className="addr-modal-icon"><i className="fa-solid fa-map-location-dot"></i></div>
                <div>
                  <h5 className="addr-modal-title">Add New Address</h5>
                  <p className="addr-modal-subtitle">Fill in your delivery details</p>
                </div>
              </div>
              <button className="addr-modal-close" onClick={() => setShowAddressModal(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="addr-modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">House / Floor / Flat Number <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${errors.house ? "is-invalid" : ""}`} name="house" value={addressForm.house} onChange={handleChange} placeholder="e.g. Flat 302, Block B" />
                    {errors.house && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.house}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Address <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${errors.address ? "is-invalid" : ""}`} name="address" value={addressForm.address} onChange={handleChange} placeholder="Street, area, building" />
                    {errors.address && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.address}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label addr-label-grey">Landmark</label>
                    <input type="text" className="form-control" name="landmark" value={addressForm.landmark} onChange={handleChange} placeholder="Optional" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">City <span className="addr-required">*</span></label>
                    <input type="text" name="city" value={addressForm.city} className={`form-control ${errors.city ? "is-invalid" : ""}`} onChange={handleChange} />
                    {errors.city && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.city}</small>}
                  </div>
                </div>
                <div className="col-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Pin Code <span className="addr-required">*</span></label>
                    <input type="text" name="pincode" value={addressForm.pincode} className={`form-control ${errors.pincode ? "is-invalid" : ""}`} onChange={handleChange} maxLength={6} inputMode="numeric" />
                    {errors.pincode && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.pincode}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">State <span className="addr-required">*</span></label>
                    <input type="text" name="state" value={addressForm.state} className={`form-control ${errors.state ? "is-invalid" : ""}`} onChange={handleChange} />
                    {errors.state && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.state}</small>}
                  </div>
                </div>
              </div>
              <div className="addr-order-for-wrap">
                <span className="addr-order-for-title">Who are you ordering for? *</span>
                <div className="addr-radio-row">
                  <div className={`addr-radio-card ${addressForm.orderFor === "self" ? "addr-radio-card--active" : ""}`} onClick={() => handleChange({ target: { name: "orderFor", value: "self" } })}>
                    <i className="fa-solid fa-user addr-radio-card-icon"></i><span>My Self</span>
                  </div>
                  <div className={`addr-radio-card ${addressForm.orderFor === "other" ? "addr-radio-card--active" : ""}`} onClick={() => handleChange({ target: { name: "orderFor", value: "other" } })}>
                    <i className="fa-solid fa-users addr-radio-card-icon"></i><span>Someone Else</span>
                  </div>
                </div>
                {errors.orderFor && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.orderFor}</small>}
              </div>
              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Recipient Name <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${errors.recipient ? "is-invalid" : ""}`} name="recipient" value={addressForm.recipient} onChange={handleChange} placeholder="Full name" />
                    {errors.recipient && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.recipient}</small>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Phone Number <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${errors.phone ? "is-invalid" : ""}`} name="phone" value={addressForm.phone} onChange={handleChange} maxLength={10} inputMode="numeric" placeholder="10-digit mobile number" />
                    {errors.phone && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{errors.phone}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <p className="addr-billing-note"><i className="fa-solid fa-circle-info me-1"></i>Billing will be done using the name on the prescription</p>
                </div>
              </div>
            </div>
            <div className="addr-modal-footer">
              <button className="addr-save-btn" onClick={saveAddress}><i className="fa-solid fa-save"></i> Save Address</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ADDRESS MODAL */}
      {showEditModal && editingAddress && (
        <div className="addr-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="addr-modal" onClick={e => e.stopPropagation()}>
            <div className="addr-modal-header">
              <div className="addr-modal-header-left">
                <div className="addr-modal-icon"><i className="fa-solid fa-pen-to-square"></i></div>
                <div>
                  <h5 className="addr-modal-title">Edit Address</h5>
                  <p className="addr-modal-subtitle">Update your delivery details</p>
                </div>
              </div>
              <button className="addr-modal-close" onClick={() => setShowEditModal(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>
            <div className="addr-modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">House / Floor / Flat Number <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${editErrors.house ? "is-invalid" : ""}`} name="house" value={editForm.house} onChange={handleEditFormChange} placeholder="Please enter here" />
                    {editErrors.house && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.house}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Address <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${editErrors.address ? "is-invalid" : ""}`} name="address" value={editForm.address} onChange={handleEditFormChange} placeholder="Please enter here" />
                    {editErrors.address && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.address}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label addr-label-grey">Landmark</label>
                    <input type="text" className="form-control" name="landmark" value={editForm.landmark} onChange={handleEditFormChange} />
                  </div>
                </div>
                <div className="col-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">City <span className="addr-required">*</span></label>
                    <input type="text" name="city" value={editForm.city} className={`form-control ${editErrors.city ? "is-invalid" : ""}`} onChange={handleEditFormChange} />
                    {editErrors.city && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.city}</small>}
                  </div>
                </div>
                <div className="col-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Pin Code <span className="addr-required">*</span></label>
                    <input type="text" name="pincode" value={editForm.pincode} className={`form-control ${editErrors.pincode ? "is-invalid" : ""}`} onChange={handleEditFormChange} maxLength={6} inputMode="numeric" />
                    {editErrors.pincode && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.pincode}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <div className="addr-form-group">
                    <label className="addr-form-label">State <span className="addr-required">*</span></label>
                    <input type="text" name="state" value={editForm.state} className={`form-control ${editErrors.state ? "is-invalid" : ""}`} onChange={handleEditFormChange} />
                    {editErrors.state && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.state}</small>}
                  </div>
                </div>
              </div>
              <div className="addr-order-for-wrap">
                <span className="addr-order-for-title">Who are you ordering for? *</span>
                <div className="addr-radio-row">
                  <div className={`addr-radio-card ${editForm.orderFor === "self" ? "addr-radio-card--active" : ""}`} onClick={() => handleEditFormChange({ target: { name: "orderFor", value: "self" } })}>
                    <i className="fa-solid fa-user addr-radio-card-icon"></i><span>My Self</span>
                  </div>
                  <div className={`addr-radio-card ${editForm.orderFor === "other" ? "addr-radio-card--active" : ""}`} onClick={() => handleEditFormChange({ target: { name: "orderFor", value: "other" } })}>
                    <i className="fa-solid fa-users addr-radio-card-icon"></i><span>Someone Else</span>
                  </div>
                </div>
                {editErrors.orderFor && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.orderFor}</small>}
              </div>
              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Recipient Name <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${editErrors.recipient ? "is-invalid" : ""}`} name="recipient" value={editForm.recipient} onChange={handleEditFormChange} placeholder="Full name" />
                    {editErrors.recipient && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.recipient}</small>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="addr-form-group">
                    <label className="addr-form-label">Phone Number <span className="addr-required">*</span></label>
                    <input type="text" className={`form-control ${editErrors.phone ? "is-invalid" : ""}`} name="phone" value={editForm.phone} onChange={handleEditFormChange} maxLength={10} inputMode="numeric" placeholder="10-digit mobile number" />
                    {editErrors.phone && <small className="addr-error-text"><i className="fa-solid fa-circle-exclamation"></i>{editErrors.phone}</small>}
                  </div>
                </div>
                <div className="col-12">
                  <p className="addr-billing-note"><i className="fa-solid fa-circle-info me-1"></i>Billing will be done using the name on the prescription</p>
                </div>
              </div>
            </div>
            <div className="addr-modal-footer">
              <button className="addr-save-btn" onClick={handleUpdateAddress}><i className="fa-solid fa-save"></i> Update Address</button>
            </div>
          </div>
        </div>
      )}

      <CustomDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onConfirm={dialog.onConfirm}
        onCancel={closeDialog}
      />

      <Footer />
    </>
  );
};

export default Checkout;